import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Folder,
    FolderPlus,
    Clapperboard,
    Pencil,
    ExternalLink,
    CircleAlert,
    Calendar,
    Image,
} from 'lucide-react';
import { Loader, ActionDropdown, DeleteModal, Pagination, type DropdownAction, Button } from '@/components/ui';
import { useFolders, useCreateFolder, useDeleteFolder, useUpdateFolder, useSearchFolders } from '@/lib/api/hooks/folder';
import type { FolderListItem } from '@/lib/api/types';
import { showToast } from '@/lib/toast';
import VideoStats from '@/pages/multimedia/videos/VideoStats';
import { Heading, Paragraph } from '@/components/ui/Typography';
import Input from '@/components/ui/Input/Input';
import { getVideoPreviewImage } from '@afsa/shared/utils'
import Search from '@/components/ui/Search';
import { useSearchFilterParams } from '@/hooks/useSearchFilterParams';

const toDisplayDate = (value: string): string => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
};

const getInitials = (fullName: string): string => {
    const parts = fullName.split(' ').filter(Boolean);
    if (!parts.length) return 'NA';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

const statusBadgeClass = (status?: 'PUBLISH' | 'DRAFT') => {
    if (status === 'PUBLISH') return 'bg-green-100 text-green-700';
    return 'bg-amber-100 text-amber-700';
};


const VideoFolders: React.FC = () => {
    const navigate = useNavigate();
    const [createFolderName, setCreateFolderName] = useState('');

    // Delete modal state
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedFolderForDelete, setSelectedFolderForDelete] = useState<{ id: string; name: string } | null>(null);

    // Edit state
    const [editingFolderId, setEditingFolderId] = useState<string | null>(null);
    const [editFolderName, setEditFolderName] = useState('');

     // ── URL-driven search / filter state ──────────────────────────────────────
                    const {
                      mode,
                      searchTerm,
                      page,
                      size,
                      setSearch,
                      setPage,
                    } = useSearchFilterParams([])
                  
                
                
                  // Search API (when searching)
                  const { data: searchData, isFetching: isSearchFetching, isError: isSearchError, refetch: refetchSearch } = useSearchFolders({
                    page, size, searchBy: searchTerm, folderType: 'VIDEO'
                  },
                  mode === "search"
                  );
                
                  // Filter API (when filtering or default)
                  const { data: filterData, isFetching: isFilterFetching, isError: isFilterError, refetch: refetchFilter } = useFolders(
                    { page, size }, 'VIDEO' 
                  );
                
                  // Select appropriate data source: search takes priority
                  const aggregateData = mode === 'search' ? searchData : filterData
                  const isFetching = mode === 'search' ? isSearchFetching : isFilterFetching
                  const isError = mode === 'search' ? isSearchError : isFilterError
                
                  const totalPages = aggregateData?.data?.totalPage ?? 1
                  const refetchData = mode === 'search' ? refetchSearch : refetchFilter
                  const emptyMessage = mode === 'search'
                    ? 'No folders found matching your search criteria.'
                    : mode === "filter" ?  'No folders found matching your filter criteria.'
                    : 'No folders found.'
          
            const folders: FolderListItem[] = aggregateData?.data?.data || [];


    const { mutate: createFolder, isPending: isCreating } = useCreateFolder();
    const { mutate: deleteFolder, isPending: isDeleting } = useDeleteFolder();
    const { mutate: updateFolder, isPending: isUpdating } = useUpdateFolder();

    const handleCreateFolder = () => {
        if (!createFolderName.trim()) {
            showToast.error('Please enter a folder name');
            return;
        }

        createFolder(
            { folderName: createFolderName.trim(), folderType: 'VIDEO' },
            {
                onSuccess: () => {
                    showToast.success('Folder created successfully');
                    setCreateFolderName('');
                },
                onError: (error: Error) => {
                    showToast.error('Failed to create folder: ' + error.message);
                }
            }
        );
    };

    const handleDeleteFolder = (id: string, name: string) => {
        setSelectedFolderForDelete({ id, name });
        setDeleteModalOpen(true);
    };

    const confirmDeleteFolder = () => {
        if (!selectedFolderForDelete) return;

        deleteFolder(selectedFolderForDelete.id, {
            onSuccess: () => {
                showToast.success('Folder deleted successfully');
                setDeleteModalOpen(false);
                setSelectedFolderForDelete(null);
            },
            onError: (error: Error) => {
                showToast.error('Failed to delete folder: ' + error.message);
            }
        });
    };

    const handleEditFolder = (folder: FolderListItem) => {
        setEditingFolderId(folder.folderId);
        setEditFolderName(folder.folderName);
    };

    const handleSaveEdit = () => {
        if (!editingFolderId) return;

        const folderName = editFolderName.trim();
        if (!folderName) {
            showToast.error('Folder name cannot be empty');
            return;
        }

        updateFolder(
            { folderId: editingFolderId, data: { folderName, folderType: 'VIDEO' } },
            {
                onSuccess: () => {
                    showToast.success('Folder updated successfully');
                    setEditingFolderId(null);
                    setEditFolderName('');
                },
                onError: (error: Error) => {
                    showToast.error('Failed to update folder: ' + error.message);
                }
            }
        );
    };

    const getDropdownActions = (folder: FolderListItem): DropdownAction[] => {
        return [
            {
                label: 'Edit Folder Name',
                onClick: (e) => {
                    e.stopPropagation();
                    handleEditFolder(folder);
                }
            },
            {
                label: 'Delete',
                onClick: (e) => {
                    e.stopPropagation();
                    handleDeleteFolder(folder.folderId, folder.folderName);
                },
                variant: 'danger' as const
            }
        ];
    };

    const isEmptyState = !isFetching && mode === 'default' && folders.length === 0;

    return (
        <>
            <div className="space-y-6">
                <VideoStats />

                <Search
                                            value={searchTerm}
                                            onSearch={setSearch}
                                            placeholder="Search folders, albums or videos..."
                                            className="w-full max-w-[50%]"
                                          />

                <div className="rounded-xl border border-[#B7DBCB] bg-[#F8FFFB] p-5">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div className="grid grid-cols-[auto_1fr] items-center gap-4">
                            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-[#E8F6EE]">
                                <FolderPlus className="h-9 w-9 text-[#0F8A47]" strokeWidth={1.25} />
                            </div>
                            <div className="space-y-2">
                                <Heading level={5} className=" leading-none font-semibold">Create a new folder</Heading>
                                <Paragraph className="!text-sm ">
                                    Group related videos into a folder to keep your media organized.
                                </Paragraph>
                            </div>
                        </div>

                        <div className="w-full lg:max-w-[760px] h-max">
                            <div className="grid grid-cols-1 items-start gap-3 md:grid-cols-[1fr_auto_auto]">
                                <div className="flex flex-col gap-2">
                                    <Input
                                        type="text"
                                        value={createFolderName}
                                        onChange={(value) => setCreateFolderName(value)}
                                        placeholder="Enter folder name..."
                                        onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
                                        $marginbottom="0"
                                        className="w-full  bg-white  focus:outline-none"
                                    />
                                    <p className="inline-flex items-center gap-2 text-sm ">
                                        <CircleAlert className="h-4 w-4" />
                                        Use clear, descriptive names to make folders easy to find.
                                    </p>
                                </div>

                                <Button
                                    onClick={handleCreateFolder}
                                    disabled={isCreating}
                                    className="inline-flex items-center justify-center gap-2 bg-[#0F8A47] font-semibold text-white hover:bg-[#0d743b] disabled:opacity-50"
                                >
                                    <Folder className="h-4 w-4" />
                                    {isCreating ? 'Creating...' : 'Create Folder'}
                                </Button>

                                <Button
                                    onClick={() => setCreateFolderName('')}
                                    className="border border-gray-200 bg-white font-semibold text-gray-700 hover:bg-gray-50"
                                >
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>

                <div>
                    {isFetching ? <Loader fullPage size="lg" variant="wine" message="Loading folders..." /> :
                                            isError ? (
                                        <div className="p-8 flex items-center justify-center  flex-col gap-4 text-center">
                                          <Paragraph className="">Error loading data.</Paragraph>
                                          <Button
                                            onClick={() => {
                                              refetchData();
                                            }}
                                            className=""
                                          >
                                            Retry
                                          </Button>
                                        </div>
                                      )  : isEmptyState  ? (
                                              <div className="text-center py-20">
                                                <div className="inline-block bg-gray-100 p-4 rounded-full mb-4">
                                                <img src="/No-post-icon.png" alt="No folders" className="mx-auto h-20 w-20 object-contain" />
                                                </div>
                                              <h3 className="mt-4 text-[2rem] leading-none font-semibold text-gray-800">
                                    {mode !== "default" ? 'No folders found' : 'No Folders Yet'}
                                  </h3>
                                                 <p className="mt-1 text-sm text-gray-500">
                                    {emptyMessage}
                                  </p>
                                                  <Button variant="primary" className="mt-4" onClick={() => navigate('/admin/multimedia/video-gallery/add')}>
                                                    Create Folder
                                                  </Button>
                                              </div>
                                            ) : folders.length > 0 ? (
                        <>
                            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
                                {folders.map((folder) => {
                                    const previewUrls = folder.previewUrls || [];
                                    const [mainPreview, sideTop, sideBottom] = [previewUrls[0], previewUrls[1], previewUrls[2]];
                                    const [mainPreviewImage, sideTopImage, sideBottomImage] = [mainPreview, sideTop, sideBottom].map((url) => getVideoPreviewImage(url));
                                    const folderStatus = folder.status || '';
                                    const ownerName =
                                        folder.updatedByName && folder.updatedByName !== 'N/A'
                                            ? folder.updatedByName
                                            : folder.createdByName;
                                    const formattedOwnerName = ownerName
                                        .split(' ')
                                        .map((name, index, array) => (index === array.length - 1 ? `${name.charAt(0)}.` : name))
                                        .join(' ');

                                    return (
                                        <div
                                            key={folder.folderId}
                                            className="group rounded-xl border border-gray-200 bg-white p-3 shadow-sm transition hover:shadow-md"
                                        >
                                            {editingFolderId === folder.folderId ? (
                                                <div className="space-y-3" onClick={(e) => e.stopPropagation()}>
                                                    <Input
                                                        type="text"
                                                        value={editFolderName}
                                                        onChange={(value) => setEditFolderName(value)}
                                                        className="w-full  focus:outline-none"
                                                        autoFocus
                                                    />
                                                    <div className="flex gap-2">
                                                        <Button
                                                            onClick={handleSaveEdit}
                                                            disabled={isUpdating}
                                                            className="flex-1 bg-[#0F8A47] text-white disabled:opacity-50"
                                                        >
                                                            {isUpdating ? 'Saving...' : 'Save'}
                                                        </Button>
                                                        <Button
                                                            onClick={() => {
                                                                setEditingFolderId(null);
                                                                setEditFolderName('');
                                                            }}
                                                            className="flex-1 bg-gray-100 text-gray-700"
                                                        >
                                                            Cancel
                                                        </Button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    <div
                                                        className="relative mb-3 grid h-28 w-full cursor-pointer grid-cols-[2fr_1fr] gap-1 overflow-hidden xl:h-32 2xl:h-52"
                                                        onClick={() => navigate(`/admin/videos/${folder.folderId}`)}
                                                    >
                                                        <div className="relative flex h-full items-center justify-center overflow-hidden rounded-md bg-gray-100">
                                                            {mainPreviewImage ? (
                                                                <img src={mainPreviewImage} alt={folder.folderName} className="h-full w-full object-cover" />
                                                            ) : (
                                                                <Image className="h-16 w-16 text-gray-400" strokeWidth={1.25} />
                                                            )}
                                                        </div>
                                                        <div className="grid grid-rows-2 gap-1">
                                                            <div className="flex items-center justify-center overflow-hidden rounded-md bg-gray-100">
                                                                {sideTopImage ? (
                                                                    <img src={sideTopImage} alt={folder.folderName} className="h-full w-full object-cover" />
                                                                ) : (
                                                                    <Image className="h-10 w-10 text-gray-400" />
                                                                )}
                                                            </div>
                                                            <div className="flex items-center justify-center overflow-hidden rounded-md bg-gray-100">
                                                                {sideBottomImage ? (
                                                                    <img src={sideBottomImage} alt={folder.folderName} className="h-full w-full object-cover" />
                                                                ) : (
                                                                    <Image className="h-10 w-10 text-gray-400" />
                                                                )}
                                                            </div>
                                                        </div>

                                                        <span className="absolute -bottom-3 left-2 inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#F0D5DB] bg-white shadow-sm">
                                                            <Folder className="h-4 w-4 text-[#7C1D31]" />
                                                        </span>
                                                    </div>

                                                    <div className="mt-1 flex items-start justify-between gap-2">
                                                        <Heading level={5} className=" leading-tight font-semibold  line-clamp-1">{folder.folderName}</Heading>
                                                        <ActionDropdown id={folder.folderId} actions={getDropdownActions(folder)} />
                                                    </div>

                                                    <p className="mt-2 inline-flex items-center gap-2 text-sm text-gray-600">
                                                        <Clapperboard className="h-4 w-4" />
                                                        <span>{folder.totalItems ?? 0} videos</span>
                                                        
                                                    </p>

                                                    <div className="mt-4 grid gap-2 text-sm 2xl:grid-cols-2">
                                                        <div>
                                                            <p className="text-gray-500">Updated</p>
                                                            <p className="mt-1 inline-flex items-center gap-1 font-medium text-gray-800">
                                                                <Calendar className="h-3.5 w-3.5" />
                                                                {toDisplayDate(folder.updatedAt || folder.createdAt)}
                                                            </p>
                                                        </div>
                                                        <div>
                                                            <p className="text-gray-500">Owner</p>
                                                            <p className="mt-1 inline-flex items-center gap-2 font-medium text-gray-800">
                                                                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#EDE9FE] text-[10px] font-semibold text-[#7C3AED]">
                                                                    {getInitials(ownerName || 'N/A')}
                                                                </span>
                                                                <span className="truncate">{formattedOwnerName || 'N/A'}</span>
                                                            </p>
                                                        </div>
                                                    </div>

                                                    <div className="mt-4 flex items-center justify-between border-t border-gray-100 pt-3">
                                                       {folderStatus ? <span className={`inline-flex rounded-md px-2.5 py-1 text-xs font-semibold ${statusBadgeClass(folderStatus)}`}>
                                                            {folderStatus === 'PUBLISH' ? 'Published' : 'Draft'}
                                                        </span> : null}
                                                        <div className="flex items-center gap-2 ml-auto">
                                                            <button
                                                                type="button"
                                                                title="Edit folder name"
                                                                onClick={() => handleEditFolder(folder)}
                                                                className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                                                            >
                                                                <Pencil className="h-4 w-4" />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                title="Open folder"
                                                                onClick={() => navigate(`/admin/videos/${folder.folderId}`)}
                                                                className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                                                            >
                                                                <ExternalLink className="h-4 w-4" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            <Pagination
                                currentPage={page}
                          totalPages={totalPages}
                          onPageChange={setPage} className="p-4"
                            />
                        </>
                    ) : (
                        <div className="py-20 text-center">
                            <img src="/No-post-icon.png" alt="No folders" className="mx-auto h-20 w-20 object-contain" />
                            <h3 className="mt-4 text-[2rem] leading-none font-semibold text-gray-800">
                                {mode !== "default" ? 'No folders found' : 'No Folders Yet'}
                            </h3>
                              <p className="mt-1 text-sm text-gray-500">
                {emptyMessage}
              </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Delete Modal */}
            <DeleteModal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={confirmDeleteFolder}
                title="Delete Folder"
                subtitle="Are you sure you want to delete this folder?"
                itemName={selectedFolderForDelete?.name}
                warningText="This action cannot be undone. All videos in this folder will also be deleted."
                isLoading={isDeleting}
            />
        </>
    );
};

export default VideoFolders;
